import { useNavigation } from '@react-navigation/core';
import { Spinner } from 'aimo-ui';
import { Center } from 'components/park/styles/styles';
import ParkAndChargeTariffSection from 'components/park/tariff/ParkAndChargeTariffSection';
import ParkTariffSection from 'components/park/tariff/ParkTariffSection';
import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import useParkingZoneWithEVCheck from 'hooks/useParkingZoneWithEVCheck';
import React from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { ParkingZoneType } from 'types/common';
import {
  AccessMethod,
  AccessType,
  ChargingStation,
  SegmentProps,
} from 'types/generatedSchemaTypes';
import { ParkNavProps } from 'types/navigation';
import { useUiStore } from 'stores/uiStore';

const SelectTariffModalContent = () => {
  const parkingZoneUid = useActiveElementStore((state) => state.activeElement);
  const { pricingSegments, pricingInfoLoading, pricingCountryCode } =
    useParkingPricingInfo(parkingZoneUid, true);
  const selectedTariff = useParkzoneStore((state) => state.selectedTariffId);
  const selectedChargingTariff = useParkzoneStore(
    (state) => state.selectedChargingPricingInfoSegmentTariffPlanId
  );

  const { data } = useParkingZoneWithEVCheck(
    parkingZoneUid,
    'cache-first',
    true
  );
  const { goBack } = useNavigation<ParkNavProps>();
  const zoneCode = data?.parkZone?.commercialZoneView?.zoneCode || undefined;
  const selectedTab = useParkzoneStore((state) => state.selectedParkingZoneTab);
  const selectedType = useActiveElementStore(
    (state) => state.activeElementType
  );

  const showChargingSection =
    selectedTab === 'PARK_AND_CHARGE' || selectedTab === 'CHARGE';

  const handleLockedSegment = () => {
    return useUiStore.getState().setSheetModal('unlockTariff');
  };

  const setChargingTariff = (chargingTariff?: SegmentProps) => {
    useParkzoneStore
      .getState()
      .setSelectedChargingPricingInfoSegment(chargingTariff);
    useParkzoneStore
      .getState()
      .setSelectedChargingPricingInfoSegmentTariffPlanId(
        chargingTariff?.tariffPlanId
      );
  };

  const setParkingTariff = (tariffId: string, tariffName: string) => {
    useParkingStore.getState().setHasManuallySelectedTariff(true);
    useParkzoneStore.getState().setSelectedTariffId(tariffId);
    useParkzoneStore.getState().setSelectedTariffName(tariffName);
  };

  const handleSetTariff = (
    tariffId: string,
    tariffName: string,
    type: ParkingZoneType,
    accessType?: AccessType,
    accessMethod?: AccessMethod,
    isUnlockValid?: boolean,
    chargingTariff?: SegmentProps
  ) => {
    if (
      (accessType === 'LOCKED' || chargingTariff?.accessType === 'LOCKED') &&
      !isUnlockValid
    ) {
      if (zoneCode) {
        useParkzoneStore.getState().setUnlockTariffItem({
          tariffId,
          tariffName,
          accessMethod,
          activeElementType: type,
          zoneCode,
          chargingTariff,
        });
        return handleLockedSegment();
      }
      return;
    }
    if (selectedType === 'PARK_AND_CHARGE') {
      if (type === 'PARKING') {
        setParkingTariff(tariffId, tariffName);
      } else if (type === 'CHARGING') {
        setChargingTariff(chargingTariff);
      }
    } else {
      useActiveElementStore.getState().setActiveElementType(type);
      setParkingTariff(tariffId, tariffName);
      setChargingTariff(chargingTariff);
    }
    goBack();
  };

  if (pricingInfoLoading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  return (
    <ScrollView nestedScrollEnabled={true}>
      <ColumnWithMargins mb={40}>
        {!data?.parkZone?.parkingMethods?.unmanagedParking &&
          selectedTab !== 'CHARGE' &&
          pricingSegments && (
            <ParkTariffSection
              publishedParkTariffs={pricingSegments}
              selectedTariffId={selectedTariff}
              countryCode={pricingCountryCode || 'SE'}
              onPress={handleSetTariff}
              zoneCode={zoneCode}
            />
          )}
        {data?.parkZone?.parkingZoneChargingStations &&
          data?.parkZone?.parkingZoneChargingStations.length > 0 &&
          showChargingSection && (
            <ParkAndChargeTariffSection
              zoneCode={zoneCode}
              chargingStations={
                data.parkZone.parkingZoneChargingStations as ChargingStation[]
              }
              countryCode={pricingCountryCode || 'SE'}
              selectedTariffId={selectedChargingTariff}
              onPress={(
                tariffId,
                tariffName,
                type,
                chargingTariff,
                isUnlockValid
              ) =>
                handleSetTariff(
                  tariffId,
                  tariffName,
                  type,
                  undefined,
                  undefined,
                  isUnlockValid,
                  chargingTariff
                )
              }
            />
          )}
      </ColumnWithMargins>
    </ScrollView>
  );
};

export default SelectTariffModalContent;
